import { Directive, forwardRef, inject, Injector, input, InputSignal, OnChanges, Provider } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { InputItemModel, InputValueType } from '@libs/form/models';
import { InputBaseComponent, PARENT_NG_CONTROL } from '../../../models';
import { DynamicInputItemFactoryService } from './dynamic-input-item-factory.service';

@Directive({
  selector: '[uiDynamicInputItem]',
  providers: [
    DynamicInputItemFactoryService,
    DynamicInputItemDirective.provideParentNgControl(),
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DynamicInputItemDirective),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => DynamicInputItemDirective),
    },
  ],
})
export class DynamicInputItemDirective<T extends InputItemModel = InputItemModel> implements OnChanges, ControlValueAccessor, Validator {
  private readonly elementFactory: DynamicInputItemFactoryService = inject(DynamicInputItemFactoryService);

  private component!: InputBaseComponent;

  public item: InputSignal<T> = input.required();

  public static provideParentNgControl(): Provider {
    return {
      provide: PARENT_NG_CONTROL,
      deps: [Injector],
      useFactory: (injector: Injector): NgControl | null => {
        return injector.get(NgControl, null);
      },
    };
  }

  public ngOnChanges(): void {
    this.component = this.elementFactory.createFormElement(this.item());
  }

  public writeValue(value: InputValueType): void {
    this.component.writeValue(value);
  }

  public registerOnChange(fn: (value: InputValueType) => void): void {
    this.component.registerOnChange(fn);
  }

  public registerOnTouched(fn: () => void): void {
    this.component.registerOnTouched(fn);
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.component.setDisabledState?.(isDisabled);
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    return this.component.validate(control);
  }
}
